import * as pdfMake from "pdfmake/build/pdfmake.js";

import { ResidentInfo, SendDocumentReplyGetModel } from "../services/SendDocuments";
import DateUtil from "@/plugins/date-util/DateUtil";

/**
 * ### 文書送付履歴のPDF操作
 */
export class SendDocumentsReportPdf {
  dateUtil: DateUtil;
  normalFont: string;

  /**
   * ### コンストラクター
   *
   * - 渡されたフォントをPdfMakeに設定する
   */
  constructor(normalFont: string) {
    this.dateUtil = new DateUtil();
    this.normalFont = normalFont;

    pdfMake.vfs = { "SendDocumentPdf.ttf": this.normalFont };
    pdfMake.fonts = {
      SendDocumentPdf: {
        normal: "SendDocumentPdf.ttf",
        bold: "SendDocumentPdf.ttf"
      }
    };

    return;
  }

  /**
   * ### 文書送付PDFを作成してダウンロードさせる
   *
   * @param items 印刷対象の文書送付
   * @param facilityName 施設名
   * @param residentName 利用者名
   * @param fileName ファイル名
   */
  public createAndDownloadPdf(items: Array<SendDocumentsReportPreviewTableItem>, facilityName: string, fileName: string): void {
    const docDefinition: any = {
      styles: this.createStyles(),
      defaultStyle: this.createDefaultStyle(),
      pageSize: "A4",
      pageOrientation: "portrait",
      pageMargins: [27, 14, 27, 27], // [left, top, right, bottom]
      info: this.createInfo(),
      content: this.createContent(items, facilityName),
      footer: this.createFooter()
    };

    pdfMake.createPdf(docDefinition).download(fileName);

    return;
  }

  /**
   * ### PDFのスタイルを作成する
   *
   * - 項目毎、または共通で使用できるスタイルを指定する
   */
  private createStyles(): any {
    return {
      datetime: {
        fontSize: 12,
        alignment: "right",
        margin: [0, 0, 0, 5]
      },
      title: {
        fontSize: 18,
        bold: true,
        alignment: "center",
        margin: [0, 0, 0, 18]
      },
      names: {
        fontSize: 12
      },
      table: {
        fontSize: 7,
        margin: [0, 10, 0, 0]
      },
      tableHeader: {
        bold: true,
        fillColor: "#EEEEEE",
        alignment: "center",
        margin: [2, 2, 2, 2]
      }
    };
  }

  /**
   * ### PDFの共通スタイルを作成する
   *
   * - 全体で共通で使用できるスタイルを指定する
   */
  private createDefaultStyle(): any {
    return {
      font: "SendDocumentPdf"
    };
  }

  /**
   * ### PDF文書情報を作成する
   */
  private createInfo(): any {
    return {
      title: "HitomeQコネクト 文書送付一覧",
      author: "KONICAMINOLA"
    };
  }

  /**
   * ### フッター情報を作成する
   *
   * - ページ番号を印字する
   */
  private createFooter(): Function {
    return (currentPage: number, pageCount: number) => {
      return { text: currentPage.toString() + " / " + pageCount + "ページ", style: { fontSize: 12, alignment: "center", margin: [0, 12, 0, 12] } };
    };
  }

  /**
   * ### 印刷情報を作成する
   *
   * - ヘッダー部
   *   - 作成日
   *   - タイトル
   *   - 施設名
   *   - 利用者名
   * - コンテンツ部(文書送付情報)
   */
  private createContent(items: Array<SendDocumentsReportPreviewTableItem>, facilityName: string): any {
    let contents: Array<any> = [];

    contents = this.createHeader(facilityName);
    contents.push(this.createTable(items));

    return contents;
  }

  /**
   * ### コンテンツのタイトル及び固定部分を作成する
   *
   * @param facilityName 施設名
   * @param residentName 利用者名
   */
  private createHeader(facilityName: string): Array<any> {
    const contents: Array<any> = [];

    // 日付
    contents.push({
      text: this.pdfCreateDateTime(),
      style: "datetime"
    });

    // タイトル
    contents.push({
      text: "HitomeQコネクト 文書送付一覧",
      style: "title"
    });

    // 施設名、利用者名
    contents.push({
      text: ["施設名：", { text: `${facilityName}`, bold: true }],
      style: "names"
    });

    return contents;
  }

  /**
   * ### 作成日時を「yyyy年 mm月 dd日」の形式の文字列にする
   */
  private pdfCreateDateTime(): string {
    const datetime: string = this.dateUtil.convertDateToString(new Date(), "yyyy年 mm月 dd日", false);
    return datetime;
  }

  /**
   * ### テーブル情報を作成する
   *
   * @param items 文書送付情報
   */
  private createTable(items: Array<SendDocumentsReportPreviewTableItem>): any {
    // テーブル
    const tableContent: any = {
      style: "table",
      table: {
        headerRows: 1,
        widths: [78, 134, 90, 20, 78, 90]
      }
    };
    const body: Array<any> = [
      [
        { text: "送付日時", style: "tableHeader" },
        { text: "送付文書", style: "tableHeader" },
        { text: "利用者\n家族", style: "tableHeader" },
        { text: "返信状態", style: "tableHeader" },
        { text: "返信日時", style: "tableHeader" },
        { text: "返信時の連絡事項", style: "tableHeader" }
      ]
    ];
    for (const item of items) {
      const residentInfo: ResidentInfo = item.residentInfo as ResidentInfo;
      body.push([
        { text: this.convertDateText(item.sendDatetime), style: { alignment: "center" } },
        item.title,
        `${residentInfo.name}（${residentInfo.room}）\n${residentInfo.familyName}（${residentInfo.familyRelation}）`,
        { text: this.convertStatusText(item), style: { alignment: "center" } },
        { text: this.convertDateText(item.datetime), style: { alignment: "center" } },
        item.message
      ]);
    }
    tableContent.table.body = body;

    return tableContent;
  }

  /**
   * ### 日付を「yyyy年mm月dd日 hh:MM」形式の文字列に変換する
   *
   * @param datetime 日付
   */
  private convertDateText(datetime: number | null): string {
    if (datetime == null) {
      return "ー";
    }

    const date: string = this.dateUtil.convertNumberToString(datetime, "yyyy年mm月dd日 hh:MM");

    return date;
  }

  /**
   * ### 文書送付の種別を文字列に変換する
   *
   * @param datetime
   */
  private convertStatusText(item: SendDocumentsReportPreviewTableItem): string {
    if (item.datetime == null) {
      return "－";
    }

    if (item.needAgreement == true) {
      return "同意";
    } else if (item.needConfirm == true) {
      return "確認";
    } else {
      return "－";
    }
  }
}

export interface SendDocumentsReportPreviewTableItem extends SendDocumentReplyGetModel {
  /** 返信日時 継承元がnumberだがnullを許容したいのでanyとする */
  datetime: any;
  /** 送付日時 */
  sendDatetime: number;
  /** タイトル */
  title: string;
  /** 確認しましたボタンを付与するか */
  needConfirm: boolean;
  /** 同意しましたボタンを付与するか */
  needAgreement: boolean;
}
